import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/ui/Card';
import DateSelect from '../components/ui/DateSelect';
import Button from '../components/ui/Button';
import Icons from '../components/Icons';
import SearchableSelect from '../components/ui/SearchableSelect';
import StaffService from '../services/StaffService';
import ProgrammeService from '../services/ProgrammeService';

export default function ProgrammesCreatePage() {
  const navigate = useNavigate();
  const programmeService = new ProgrammeService();
  const staffService = useMemo(() => new StaffService(), []);
  const staffList = useMemo(() => staffService.list(), [staffService]);
  const staffOptions = useMemo(
    () =>
      staffList.map((staff) => ({
        value: staff.name,
        label: staff.name,
        meta: staff.role || staff.title || ''
      })),
    [staffList]
  );
  const [form, setForm] = useState({
    name: '',
    startDate: new Date().toISOString().slice(0, 10),
    endDate: '',
    days: [],
    time: '',
    capacity: '',
    location: '',
    staff: '',
    staffAvailability: '',
    courseFee: '',
    eventFee: '',
    otherFee: ''
  });
  const [formError, setFormError] = useState('');
  const [templates, setTemplates] = useState([
    { id: `tpl-${Math.random().toString(36).slice(2, 8)}`, title: '', duration: '', focus: '' }
  ]);
  const [documents, setDocuments] = useState([
    {
      id: `doc-${Math.random().toString(36).slice(2, 8)}`,
      name: '',
      file: null,
      expiryDate: '',
      linkUrl: '',
      archived: false
    }
  ]);
  const [lessonPlans, setLessonPlans] = useState([
    {
      id: `plan-${Math.random().toString(36).slice(2, 8)}`,
      title: '',
      date: '',
      file: null,
      notes: '',
      linkUrl: '',
      archived: false
    }
  ]);
  const [linkStatus, setLinkStatus] = useState('');

  const dayOptions = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const activeDocuments = documents.filter((doc) => !doc.archived);
  const archivedDocuments = documents.filter((doc) => doc.archived);
  const activeLessonPlans = lessonPlans.filter((plan) => !plan.archived);
  const archivedLessonPlans = lessonPlans.filter((plan) => plan.archived);

  const toggleDay = (day) => {
    setForm((prev) => ({
      ...prev,
      days: prev.days.includes(day) ? prev.days.filter((d) => d !== day) : [...prev.days, day]
    }));
  };

  const addTemplate = () => {
    setTemplates((prev) => [
      ...prev,
      { id: `tpl-${Math.random().toString(36).slice(2, 8)}`, title: '', duration: '', focus: '' }
    ]);
  };

  const removeTemplate = (id) => {
    setTemplates((prev) => prev.filter((item) => item.id !== id));
  };

  const updateTemplate = (id, updates) => {
    setTemplates((prev) => prev.map((item) => (item.id === id ? { ...item, ...updates } : item)));
  };

  const addDocument = () => {
    setDocuments((prev) => [
      ...prev,
      {
        id: `doc-${Math.random().toString(36).slice(2, 8)}`,
        name: '',
        file: null,
        expiryDate: '',
        linkUrl: '',
        archived: false
      }
    ]);
  };

  const removeDocument = (id) => {
    setDocuments((prev) => prev.filter((doc) => doc.id !== id));
  };

  const updateDocument = (id, updates) => {
    setDocuments((prev) => prev.map((doc) => (doc.id === id ? { ...doc, ...updates } : doc)));
  };

  const addLessonPlan = () => {
    setLessonPlans((prev) => [
      ...prev,
      {
        id: `plan-${Math.random().toString(36).slice(2, 8)}`,
        title: '',
        date: '',
        file: null,
        notes: '',
        linkUrl: '',
        archived: false
      }
    ]);
  };

  const removeLessonPlan = (id) => {
    setLessonPlans((prev) => prev.filter((plan) => plan.id !== id));
  };

  const updateLessonPlan = (id, updates) => {
    setLessonPlans((prev) => prev.map((plan) => (plan.id === id ? { ...plan, ...updates } : plan)));
  };

  const handlePasteLink = async (applyLink) => {
    if (!navigator?.clipboard?.readText) {
      setLinkStatus('Clipboard access is not available in this browser.');
      return;
    }
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        applyLink(text.trim());
        setLinkStatus('Link pasted from clipboard.');
      }
    } catch (error) {
      setLinkStatus('Unable to read clipboard. Please paste manually.');
    }
  };

  const handleAddProgramme = (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setFormError('Programme name is required.');
      return;
    }
    if (!form.capacity) {
      setFormError('Capacity is required.');
      return;
    }
    if (form.days.length === 0) {
      setFormError('Select at least one day.');
      return;
    }

    const validTemplates = templates.filter((tpl) => tpl.title.trim());
    const validDocuments = documents
      .filter((doc) => doc.name.trim() || doc.file || doc.linkUrl)
      .map((doc) => ({
        id: doc.id,
        name: doc.name.trim() || (doc.file ? doc.file.name : 'Document'),
        expiryDate: doc.expiryDate || null,
        fileName: doc.file ? doc.file.name : null,
        linkUrl: doc.linkUrl || '',
        archived: Boolean(doc.archived),
        uploadedAt: new Date().toISOString()
      }));
    const validLessonPlans = lessonPlans
      .filter((plan) => plan.title.trim() || plan.file || plan.linkUrl)
      .map((plan) => ({
        id: plan.id,
        title: plan.title.trim() || (plan.file ? plan.file.name : 'Lesson plan'),
        date: plan.date || null,
        fileName: plan.file ? plan.file.name : null,
        notes: plan.notes || '',
        linkUrl: plan.linkUrl || '',
        archived: Boolean(plan.archived),
        uploadedAt: new Date().toISOString()
      }));

    programmeService.add({
      id: `prog-${Math.random().toString(36).slice(2, 9)}`,
      name: form.name.trim(),
      day: form.days.join('/'),
      time: form.time || 'TBD',
      startDate: form.startDate,
      endDate: form.endDate || null,
      capacity: Number(form.capacity),
      enrolled: 0,
      staff: form.staff || 'Unassigned',
      location: form.location || 'TBD',
      days: form.days,
      staffAvailability: form.staffAvailability || '',
      fees: {
        courseFee: Number(form.courseFee || 0),
        eventFee: Number(form.eventFee || 0),
        otherFee: Number(form.otherFee || 0)
      },
      templates: validTemplates.map((tpl) => ({
        id: tpl.id,
        title: tpl.title.trim(),
        duration: tpl.duration.trim(),
        focus: tpl.focus.trim()
      })),
      documents: validDocuments,
      lessonPlans: validLessonPlans
    });

    navigate('/programmes/overview');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Add Programme</h2>
          <p className="sp-muted">Define schedule, capacity, location, fees, and assigned support worker.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => navigate('/programmes/overview')}>
            Back to overview
          </Button>
        </div>
      </div>

      <Card className="p-6">
        <form onSubmit={handleAddProgramme} className="space-y-4">
          {formError && <div className="sp-panel sp-panel--muted p-3 text-sm">{formError}</div>}

          <div>
            <label className="sp-label">Programme name</label>
            <input
              className="sp-input mt-2 w-full"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              placeholder="e.g. Cooking Workshop"
              required
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Start date</label>
              <DateSelect
                value={form.startDate}
                onChange={(value) => setForm({ ...form, startDate: value })}
                className="mt-2 w-full"
              />
            </div>
            <div>
              <label className="sp-label">End date</label>
              <DateSelect
                value={form.endDate}
                onChange={(value) => setForm({ ...form, endDate: value })}
                className="mt-2 w-full"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Days</label>
              <div className="mt-2 grid grid-cols-4 gap-2">
                {dayOptions.map((day) => (
                  <label key={day} className="flex items-center gap-2 text-sm sp-muted">
                    <input
                      type="checkbox"
                      checked={form.days.includes(day)}
                      onChange={() => toggleDay(day)}
                    />
                    <span>{day}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="sp-label">Time</label>
              <input
                className="sp-input mt-2 w-full"
                value={form.time}
                onChange={(event) => setForm({ ...form, time: event.target.value })}
                placeholder="e.g. 16:00-18:00"
              />
              <label className="sp-label mt-4 block">Capacity</label>
              <input
                type="number"
                min="0"
                className="sp-input mt-2 w-full"
                value={form.capacity}
                onChange={(event) => setForm({ ...form, capacity: event.target.value })}
                placeholder="e.g. 20"
                required
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Location</label>
              <input
                className="sp-input mt-2 w-full"
                value={form.location}
                onChange={(event) => setForm({ ...form, location: event.target.value })}
                placeholder="e.g. Studio A"
              />
            </div>
            <div>
              <label className="sp-label">Assign support worker</label>
              <SearchableSelect
                value={form.staff}
                options={staffOptions}
                placeholder="Search staff"
                emptyLabel="No staff found."
                ariaLabel="Search support worker"
                className="mt-2"
                onChange={(value) => setForm({ ...form, staff: value })}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="sp-label">Course fees</label>
              <input
                type="number"
                min="0"
                className="sp-input mt-2 w-full"
                value={form.courseFee}
                onChange={(event) => setForm({ ...form, courseFee: event.target.value })}
                placeholder="e.g. 120"
              />
            </div>
            <div>
              <label className="sp-label">Event fees</label>
              <input
                type="number"
                min="0"
                className="sp-input mt-2 w-full"
                value={form.eventFee}
                onChange={(event) => setForm({ ...form, eventFee: event.target.value })}
                placeholder="e.g. 45"
              />
            </div>
            <div>
              <label className="sp-label">Other fees</label>
              <input
                type="number"
                min="0"
                className="sp-input mt-2 w-full"
                value={form.otherFee}
                onChange={(event) => setForm({ ...form, otherFee: event.target.value })}
                placeholder="e.g. 25"
              />
            </div>
          </div>

          <div>
            <label className="sp-label">Staff availability</label>
            <input
              className="sp-input mt-2 w-full"
              value={form.staffAvailability}
              onChange={(event) => setForm({ ...form, staffAvailability: event.target.value })}
              placeholder="e.g. Mon/Wed 15:00-18:00"
            />
          </div>

          <div className="sp-panel sp-panel--muted p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="sp-value" style={{ fontWeight: 600 }}>Session Templates</h4>
                <p className="text-xs sp-muted">Define repeatable lesson templates for this programme.</p>
              </div>
              <Button size="sm" variant="secondary" type="button" onClick={addTemplate}>
                Add template
              </Button>
            </div>
            <div className="space-y-3 mt-4">
              {templates.map((template, index) => (
                <div key={template.id} className="grid md:grid-cols-3 gap-3">
                  <div>
                    <label className="sp-label">Title</label>
                    <input
                      className="sp-input mt-2 w-full"
                      value={template.title}
                      onChange={(event) => updateTemplate(template.id, { title: event.target.value })}
                      placeholder={`Session ${index + 1}`}
                    />
                  </div>
                  <div>
                    <label className="sp-label">Duration</label>
                    <input
                      className="sp-input mt-2 w-full"
                      value={template.duration}
                      onChange={(event) => updateTemplate(template.id, { duration: event.target.value })}
                      placeholder="e.g. 90 mins"
                    />
                  </div>
                  <div>
                    <label className="sp-label">Focus area</label>
                    <input
                      className="sp-input mt-2 w-full"
                      value={template.focus}
                      onChange={(event) => updateTemplate(template.id, { focus: event.target.value })}
                      placeholder="e.g. Life skills"
                    />
                  </div>
                  {templates.length > 1 && (
                    <div className="md:col-span-3 flex justify-end">
                      <Button size="sm" variant="ghost" type="button" onClick={() => removeTemplate(template.id)}>
                        Remove template
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="sp-panel sp-panel--muted p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="sp-value" style={{ fontWeight: 600 }}>Programme Documents</h4>
                <p className="text-xs sp-muted">Upload supporting documents with optional expiry dates.</p>
              </div>
              <Button size="sm" variant="secondary" type="button" onClick={addDocument}>
                Add document
              </Button>
            </div>
            <div className="space-y-3 mt-4">
              {activeDocuments.map((doc) => (
                <div key={doc.id} className="grid md:grid-cols-3 gap-3">
                  <div>
                    <label className="sp-label">Document name</label>
                    <input
                      className="sp-input mt-2 w-full"
                      value={doc.name}
                      onChange={(event) => updateDocument(doc.id, { name: event.target.value })}
                      placeholder="e.g. Risk assessment"
                    />
                  </div>
                  <div>
                    <label className="sp-label">Upload</label>
                    <input
                      type="file"
                      className="sp-input mt-2 w-full"
                      onChange={(event) =>
                        updateDocument(doc.id, { file: event.target.files?.[0] || null })
                      }
                    />
                    {doc.file && (
                      <p className="text-xs sp-muted mt-1">{doc.file.name}</p>
                    )}
                  </div>
                  <div>
                    <label className="sp-label">Expiry date</label>
                    <DateSelect
                      value={doc.expiryDate}
                      onChange={(value) => updateDocument(doc.id, { expiryDate: value })}
                      className="mt-2 w-full"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="sp-label">Share link</label>
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        className="sp-input w-full"
                        value={doc.linkUrl || ''}
                        onChange={(event) => updateDocument(doc.id, { linkUrl: event.target.value })}
                        placeholder="https://drive.google.com/..."
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        aria-label="Paste link from clipboard"
                        onClick={() => handlePasteLink((text) => updateDocument(doc.id, { linkUrl: text }))}
                      >
                        <Icons.Clipboard />
                      </Button>
                    </div>
                    <p className="text-xs sp-muted mt-1">Use Google Drive or similar and paste a share link.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="sp-label">Archive</label>
                    <input
                      type="checkbox"
                      checked={Boolean(doc.archived)}
                      onChange={(event) => updateDocument(doc.id, { archived: event.target.checked })}
                      aria-label="Archive document"
                    />
                  </div>
                  {documents.length > 1 && (
                    <div className="md:col-span-3 flex justify-end">
                      <Button size="sm" variant="ghost" type="button" onClick={() => removeDocument(doc.id)}>
                        Remove document
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              {archivedDocuments.length > 0 && (
                <div className="sp-panel sp-panel--muted p-3 text-sm">
                  <div className="sp-value" style={{ fontWeight: 600 }}>Archived documents</div>
                  <div className="mt-2 space-y-2">
                    {archivedDocuments.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between gap-2">
                        <div>
                          <div className="sp-value" style={{ fontWeight: 600 }}>{doc.name || doc.file?.name || 'Document'}</div>
                          <div className="text-xs sp-muted">{doc.linkUrl || 'No link added'}</div>
                        </div>
                        <Button size="sm" variant="secondary" type="button" onClick={() => updateDocument(doc.id, { archived: false })}>
                          Restore
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="sp-panel sp-panel--muted p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="sp-value" style={{ fontWeight: 600 }}>Lesson Plans</h4>
                <p className="text-xs sp-muted">Upload lesson plans and resources for this programme.</p>
              </div>
              <Button size="sm" variant="secondary" type="button" onClick={addLessonPlan}>
                Add lesson plan
              </Button>
            </div>
            <div className="space-y-3 mt-4">
              {activeLessonPlans.map((plan) => (
                <div key={plan.id} className="grid md:grid-cols-3 gap-3">
                  <div>
                    <label className="sp-label">Title</label>
                    <input
                      className="sp-input mt-2 w-full"
                      value={plan.title}
                      onChange={(event) => updateLessonPlan(plan.id, { title: event.target.value })}
                      placeholder="e.g. Week 1 plan"
                    />
                  </div>
                  <div>
                    <label className="sp-label">Session date</label>
                    <DateSelect
                      value={plan.date}
                      onChange={(value) => updateLessonPlan(plan.id, { date: value })}
                      className="mt-2 w-full"
                    />
                  </div>
                  <div>
                    <label className="sp-label">Upload</label>
                    <input
                      type="file"
                      className="sp-input mt-2 w-full"
                      onChange={(event) =>
                        updateLessonPlan(plan.id, { file: event.target.files?.[0] || null })
                      }
                    />
                    {plan.file && <p className="text-xs sp-muted mt-1">{plan.file.name}</p>}
                  </div>
                  <div className="md:col-span-2">
                    <label className="sp-label">Share link</label>
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        className="sp-input w-full"
                        value={plan.linkUrl || ''}
                        onChange={(event) => updateLessonPlan(plan.id, { linkUrl: event.target.value })}
                        placeholder="https://drive.google.com/..."
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        aria-label="Paste link from clipboard"
                        onClick={() => handlePasteLink((text) => updateLessonPlan(plan.id, { linkUrl: text }))}
                      >
                        <Icons.Clipboard />
                      </Button>
                    </div>
                    <p className="text-xs sp-muted mt-1">Use Google Drive or similar and paste a share link.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="sp-label">Archive</label>
                    <input
                      type="checkbox"
                      checked={Boolean(plan.archived)}
                      onChange={(event) => updateLessonPlan(plan.id, { archived: event.target.checked })}
                      aria-label="Archive lesson plan"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className="sp-label">Notes</label>
                    <textarea
                      className="sp-textarea mt-2 w-full"
                      rows={2}
                      value={plan.notes}
                      onChange={(event) => updateLessonPlan(plan.id, { notes: event.target.value })}
                      placeholder="Key outcomes, resources, or homework."
                    />
                  </div>
                  {lessonPlans.length > 1 && (
                    <div className="md:col-span-3 flex justify-end">
                      <Button size="sm" variant="ghost" type="button" onClick={() => removeLessonPlan(plan.id)}>
                        Remove lesson plan
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              {archivedLessonPlans.length > 0 && (
                <div className="sp-panel sp-panel--muted p-3 text-sm">
                  <div className="sp-value" style={{ fontWeight: 600 }}>Archived lesson plans</div>
                  <div className="mt-2 space-y-2">
                    {archivedLessonPlans.map((plan) => (
                      <div key={plan.id} className="flex items-center justify-between gap-2">
                        <div>
                          <div className="sp-value" style={{ fontWeight: 600 }}>{plan.title || plan.file?.name || 'Lesson plan'}</div>
                          <div className="text-xs sp-muted">{plan.linkUrl || 'No link added'}</div>
                        </div>
                        <Button size="sm" variant="secondary" type="button" onClick={() => updateLessonPlan(plan.id, { archived: false })}>
                          Restore
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {linkStatus && <div className="text-xs sp-muted mt-2">{linkStatus}</div>}
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={() => navigate('/programmes/overview')}>
              Cancel
            </Button>
            <Button type="submit">Add programme</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
